import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  ArrowLeft, ArrowRight, CheckCircle, Warning, FileArrowUp, Plus, Trash,
  Buildings, Users, Calculator, Calendar, Package, ListChecks, Sparkle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import ContactSearch from '../components/ui/ContactSearch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'infos', label: 'Informations', icon: Buildings },
  { key: 'intervenants', label: 'Intervenants', icon: Users },
  { key: 'lots', label: 'Lots', icon: Package },
  { key: 'finances', label: 'Finances', icon: Calculator },
  { key: 'chantier', label: 'État', icon: Calendar },
  { key: 'documents', label: 'Documents', icon: FileArrowUp },
  { key: 'instructions', label: 'Instructions', icon: ListChecks },
];

const DOC_CATEGORIES = [
  { key: 'administratif', label: 'Documents administratifs', hint: 'Arrêté de péril, permis de construire, déclarations, actes notariés…' },
  { key: 'technique', label: 'Documents techniques', hint: "Rapports d'expertise, diagnostics (amiante, plomb, structure), notes BET…" },
  { key: 'dpgf_devis', label: 'DPGF, devis et marchés', hint: "DPGF Excel, devis, actes d'engagement, marchés signés…" },
  // Iter 89 — Import structuré direct via /dpgf/import (parser openpyxl backend)
  // au lieu de l'analyse IA. Recommandé si l'utilisateur a un xlsx propre.
  { key: 'dpgf_excel_struct', label: 'DPGF Excel — Import structuré (recommandé si dispo)', hint: 'Fichier .xlsx/.xls avec colonnes Lot, Désignation, Unité, Quantité, PU. Sera parsé directement (sans analyse IA) — plus rapide et plus précis qu\'un PDF.', requires_excel: true, structured_endpoint: 'dpgf' },
  { key: 'factures', label: 'Factures et situations', hint: 'Factures, situations de travaux, DGD…' },
  { key: 'comptes_rendus', label: 'Comptes rendus de chantier', hint: 'Comptes rendus précédents (Word, PDF), PV de réunions…' },
  { key: 'planning', label: 'Planning', hint: 'Planning Excel, MS Project, PDF…' },
  // Iter 89 — Import structuré direct via /planning/import (parser openpyxl backend)
  { key: 'planning_excel_struct', label: 'Planning Excel — Import structuré (recommandé si dispo)', hint: 'Fichier .xlsx avec colonnes Nom/Tâche, Date début, Durée ou Date fin, Lot, Entreprise, Avancement %. Sera importé directement — dates réelles préservées.', requires_excel: true, structured_endpoint: 'planning' },
  { key: 'plans', label: 'Plans', hint: "Plans d'architecte (PDF, DWG), relevés géomètre…" },
  { key: 'assurance', label: 'Assurances', hint: 'Attestations RC Pro, décennale, DO…' },
  { key: 'photos', label: 'Photos du chantier', hint: 'État actuel, avant travaux, pendant travaux…' },
  { key: 'autre', label: 'Autres documents', hint: 'Tout document utile non classé…' },
];

// Iter 89 — Catégories à import structuré (Excel direct, sans analyse IA).
// Au handleUpload, on stocke le File brut au lieu de pousser vers /upload.
// Au handleSubmit, on appelle l'endpoint dédié AVANT le redirect final.
const STRUCTURED_IMPORT_CATEGORIES = DOC_CATEGORIES.filter((c) => c.structured_endpoint).map((c) => c.key);
const STRUCTURED_ENDPOINT_BY_CAT = Object.fromEntries(
  DOC_CATEGORIES.filter((c) => c.structured_endpoint).map((c) => [c.key, c.structured_endpoint])
);

const initialData = {
  nom: '',
  adresse: '',
  type_projet: 'rehabilitation',
  phase: 'EXE',
  nature_batiment: '',
  date_debut: '',
  date_fin: '',
  maitre_ouvrage: { nom: '', type: 'syndic', email: '', telephone: '' },
  proprietaires: [],
  equipe: [],
  lots: [],
  finances: { montant_marche: '', facture: '', paye: '', litige: '', retenue: 'nsp' },
  avancement: 0,
  problemes: '',
  frequence_reunions: 'hebdomadaire',
  jour_reunion: 'Mardi',
  heure_reunion: '14:00',
  prochaine_reunion: '',
  contexte_reglementaire: '',
  contexte: '',
  instructions: '',
  // files: { cat -> [{id, name, size, uploaded}] }
  files: {},
};

function formatSize(n) {
  if (!n) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}

export default function ImportProjectWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [chantierId, setChantierId] = useState(null);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));
  const updateNested = (key, patch) => setData((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const canNext = (() => {
    if (step === 0) return data.nom.trim() && data.adresse.trim();
    if (step === 1) return data.maitre_ouvrage.nom.trim();
    return true;
  })();

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  // ========== UPLOAD (vers /chantiers/TEMP/upload impossible car pas d'id)
  // Stratégie : on crée le chantier d'abord puis on uploade. Mais ici on veut
  // l'upload avant création. Solution : créer un chantier draft minimal dès la
  // 1ère pièce uploadée, puis réutiliser son id pour tous les autres uploads,
  // puis le finaliser à la soumission.
  const [draftId, setDraftId] = useState(null);

  const ensureDraft = async () => {
    if (draftId) return draftId;
    const nom = data.nom.trim() || '(Import en cours)';
    const adresse = data.adresse.trim() || 'Adresse à compléter';
    const { data: created } = await axios.post(
      `${API_URL}/api/chantiers`,
      { name: nom, address: adresse, owner_name: data.maitre_ouvrage.nom || 'À préciser', status: 'prospection' },
      { withCredentials: true }
    );
    setDraftId(created.id);
    return created.id;
  };

  const handleUpload = async (catKey, fileList) => {
    if (!fileList || fileList.length === 0) return;

    // Iter 89 — Catégories à import structuré : on garde le File brut en mémoire
    // (pour appel /dpgf/import ou /planning/import au handleSubmit final) sans
    // passer par /upload. Validation extension xlsx/xls.
    if (STRUCTURED_IMPORT_CATEGORIES.includes(catKey)) {
      for (const file of Array.from(fileList)) {
        const ext = (file.name || '').toLowerCase();
        if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
          toast.error(`Format non supporté pour import structuré : ${file.name} (attendu .xlsx ou .xls)`);
          continue;
        }
        const tmpId = `${catKey}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setData((d) => ({
          ...d,
          files: {
            ...d.files,
            [catKey]: [
              ...(d.files[catKey] || []),
              { tmpId, name: file.name, size: file.size, fileObject: file, pending_structured_import: true, uploaded: true },
            ],
          },
        }));
      }
      return;
    }

    const id = await ensureDraft();
    for (const file of Array.from(fileList)) {
      const tmpId = `${catKey}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setData((d) => ({
        ...d,
        files: {
          ...d.files,
          [catKey]: [...(d.files[catKey] || []), { tmpId, name: file.name, size: file.size, uploading: true }],
        },
      }));
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('doc_type', catKey);
        const { data: doc } = await axios.post(
          `${API_URL}/api/chantiers/${id}/upload`,
          form,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setData((d) => ({
          ...d,
          files: {
            ...d.files,
            [catKey]: (d.files[catKey] || []).map((f) =>
              f.tmpId === tmpId ? { ...f, id: doc.id, uploading: false, uploaded: true } : f
            ),
          },
        }));
      } catch (e) {
        setData((d) => ({
          ...d,
          files: {
            ...d.files,
            [catKey]: (d.files[catKey] || []).map((f) =>
              f.tmpId === tmpId ? { ...f, uploading: false, error: true } : f
            ),
          },
        }));
        toast.error(`Échec upload : ${file.name}`);
      }
    }
  };

  const removeFile = (catKey, tmpId) => {
    setData((d) => ({
      ...d,
      files: { ...d.files, [catKey]: (d.files[catKey] || []).filter((f) => f.tmpId !== tmpId) },
    }));
  };

  const totalFiles = Object.values(data.files).reduce((acc, arr) => acc + (arr?.length || 0), 0);

  // Soumission finale
  const handleSubmit = async () => {
    if (!data.nom.trim() || !data.adresse.trim()) {
      toast.error('Nom et adresse sont obligatoires');
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      // Collecte tous les document_ids uploadés
      const document_ids = [];
      Object.values(data.files).forEach((arr) => {
        (arr || []).forEach((f) => {
          if (f.id && f.uploaded) document_ids.push(f.id);
        });
      });

      const payload = {
        nom: data.nom.trim(),
        adresse: data.adresse.trim(),
        type_projet: data.type_projet,
        phase: data.phase,
        nature_batiment: data.nature_batiment,
        date_debut: data.date_debut || null,
        date_fin: data.date_fin || null,
        maitre_ouvrage: data.maitre_ouvrage,
        proprietaires: data.proprietaires,
        equipe: data.equipe,
        lots: data.lots.map((l) => ({
          code: l.code || '',
          nom: l.nom || '',
          entreprise: l.entreprise || '',
          email: l.email || '',
          montant: Number(l.montant) || 0,
        })),
        finances: {
          montant_marche: Number(data.finances.montant_marche) || 0,
          facture: Number(data.finances.facture) || 0,
          paye: Number(data.finances.paye) || 0,
          litige: data.finances.litige,
          retenue: data.finances.retenue,
        },
        avancement: Number(data.avancement) || 0,
        problemes: data.problemes,
        frequence_reunions: data.frequence_reunions,
        jour_reunion: data.jour_reunion,
        heure_reunion: data.heure_reunion,
        prochaine_reunion: data.prochaine_reunion || null,
        contexte_reglementaire: data.contexte_reglementaire,
        contexte: data.contexte,
        instructions: data.instructions,
        document_ids,
        draft_id: draftId,  // si un draft a été créé via upload, le backend pourra le réutiliser
      };

      // Si un draft existe déjà (car upload de fichiers), on met à jour ce chantier
      // puis on lance l'analyse IA. Sinon on utilise l'endpoint d'import.
      let newChantierId = null;
      if (draftId) {
        // Basculer les documents du draft vers un import complet : on update le
        // draft avec les champs manuels, on rattache les docs déjà uploadés.
        // Solution simple : on envoie l'import-complet avec draft_id pour que le
        // backend mette à jour ce chantier au lieu d'en créer un nouveau.
        const { data: result } = await axios.post(
          `${API_URL}/api/chantiers/import-complet?draft_id=${draftId}`,
          payload,
          { withCredentials: true }
        );
        newChantierId = result.chantier_id;
      } else {
        const { data: result } = await axios.post(
          `${API_URL}/api/chantiers/import-complet`,
          payload,
          { withCredentials: true }
        );
        newChantierId = result.chantier_id;
      }
      toast.success('Chantier créé — analyse en cours');

      // Iter 89 — Import structuré des fichiers Excel (DPGF, Planning) APRÈS
      // création du chantier. Best-effort : un échec n'empêche pas le redirect.
      for (const cat of STRUCTURED_IMPORT_CATEGORIES) {
        const files = (data.files[cat] || []).filter((f) => f.fileObject && !f.imported);
        if (files.length === 0) continue;
        const endpointKey = STRUCTURED_ENDPOINT_BY_CAT[cat];
        const url = `${API_URL}/api/chantiers/${newChantierId}/${endpointKey}/import`;
        for (const f of files) {
          try {
            const form = new FormData();
            form.append('file', f.fileObject);
            await axios.post(url, form, {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(`${endpointKey === 'dpgf' ? 'DPGF' : 'Planning'} importé : ${f.name}`);
          } catch (importErr) {
            // eslint-disable-next-line no-console
            console.error('[structured import]', endpointKey, f.name, importErr);
            const detail = importErr?.response?.data?.detail;
            toast.error(`Import ${endpointKey} échoué (${f.name}) : ${typeof detail === 'string' ? detail : 'voir console'}`);
          }
        }
      }

      setChantierId(newChantierId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setSubmitting(false);
    }
  };

  if (chantierId) return <ImportProgress chantierId={chantierId} />;

  const StepIcon = STEPS[step].icon;

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="import-wizard-page">
        <Link to="/chantiers/new" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} />
          Retour
        </Link>

        <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Importer un dossier existant
        </h1>
        <p className="text-slate-600 mb-8">
          Renseignez les informations, uploadez vos documents — l'IA extrait tout et pré-remplit l'application.
        </p>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto">
          {STEPS.map((s, i) => {
            const Ic = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.key} className="flex items-center">
                <button
                  onClick={() => setStep(i)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                    active ? 'bg-[#C75B39] text-white' : done ? 'bg-[#FFF2EB] text-[#C75B39]' : 'bg-slate-100 text-slate-500'
                  }`}
                  data-testid={`step-${s.key}`}
                >
                  <Ic size={14} weight="bold" />
                  <span>{i + 1}. {s.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-300" />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="card-swiss p-6 lg:p-8 mb-6 min-h-[400px]">
          <div className="flex items-center gap-3 mb-6">
            <StepIcon size={24} className="text-[#C75B39]" weight="bold" />
            <h2 className="text-xl font-semibold">{STEPS[step].label}</h2>
          </div>

          {step === 0 && <Step1Infos data={data} update={update} />}
          {step === 1 && <Step2Intervenants data={data} update={update} updateNested={updateNested} />}
          {step === 2 && <Step3Lots data={data} update={update} />}
          {step === 3 && <Step4Finances data={data} updateNested={updateNested} />}
          {step === 4 && <Step5Chantier data={data} update={update} />}
          {step === 5 && <Step6Documents data={data} onUpload={handleUpload} onRemove={removeFile} totalFiles={totalFiles} />}
          {step === 6 && <Step7Instructions data={data} update={update} totalFiles={totalFiles} />}
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={goPrev} disabled={step === 0} className="rounded-none" data-testid="btn-prev">
            <ArrowLeft size={16} className="mr-2" /> Précédent
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={goNext} disabled={!canNext} className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none" data-testid="btn-next">
              Suivant <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none"
              data-testid="btn-submit-import"
            >
              {submitting ? 'Import en cours…' : (<><Sparkle size={16} className="mr-2" weight="fill" /> Lancer l'import et l'analyse IA</>)}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}

// ==================== STEPS ====================

function Step1Infos({ data, update }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        Renseignez les informations de base. L'IA complétera le reste depuis vos documents.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Label>Nom du chantier *</Label>
          <Input
            value={data.nom}
            onChange={(e) => update({ nom: e.target.value })}
            placeholder="Ex: 129 Rue Gabriel Péri — Réhabilitation"
            className="mt-1 rounded-none border-slate-300"
            data-testid="input-nom"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Adresse du chantier *</Label>
          <Input
            value={data.adresse}
            onChange={(e) => update({ adresse: e.target.value })}
            placeholder="Adresse complète"
            className="mt-1 rounded-none border-slate-300"
            data-testid="input-adresse"
          />
        </div>
        <div>
          <Label>Type d'opération *</Label>
          <Select value={data.type_projet} onValueChange={(v) => update({ type_projet: v })}>
            <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="select-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rehabilitation">Réhabilitation</SelectItem>
              <SelectItem value="peril">Mise en sécurité / Péril</SelectItem>
              <SelectItem value="neuf">Construction neuve</SelectItem>
              <SelectItem value="renovation">Rénovation</SelectItem>
              <SelectItem value="extension">Extension</SelectItem>
              <SelectItem value="amenagement">Aménagement intérieur</SelectItem>
              <SelectItem value="changement_usage">Changement d'usage</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Phase actuelle *</Label>
          <Select value={data.phase} onValueChange={(v) => update({ phase: v })}>
            <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="select-phase">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ESQ">Esquisse</SelectItem>
              <SelectItem value="APS">Avant-Projet Sommaire</SelectItem>
              <SelectItem value="APD">Avant-Projet Définitif</SelectItem>
              <SelectItem value="PRO">Projet</SelectItem>
              <SelectItem value="DCE">Consultation (DCE)</SelectItem>
              <SelectItem value="ACT">Passation des Marchés</SelectItem>
              <SelectItem value="EXE">Exécution des travaux</SelectItem>
              <SelectItem value="OPR">Réception</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Nature du bâtiment</Label>
          <Input
            value={data.nature_batiment}
            onChange={(e) => update({ nature_batiment: e.target.value })}
            placeholder="Ex: Immeuble R+3 sur sous-sol, copropriété 18 lots"
            className="mt-1 rounded-none border-slate-300"
          />
        </div>
        <div>
          <Label>Date de début</Label>
          <Input type="date" value={data.date_debut} onChange={(e) => update({ date_debut: e.target.value })} className="mt-1 rounded-none border-slate-300" />
        </div>
        <div>
          <Label>Date de fin prévisionnelle</Label>
          <Input type="date" value={data.date_fin} onChange={(e) => update({ date_fin: e.target.value })} className="mt-1 rounded-none border-slate-300" />
        </div>
      </div>
    </div>
  );
}

function Step2Intervenants({ data, update, updateNested }) {
  const moa = data.maitre_ouvrage;
  return (
    <div className="space-y-8">
      <p className="text-sm text-slate-600">
        L'IA pourra aussi extraire ces informations depuis vos documents.
      </p>

      {/* MOA */}
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800 mb-3">Maître d'ouvrage</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Nom du maître d'ouvrage *</Label>
            <Input value={moa.nom} onChange={(e) => updateNested('maitre_ouvrage', { nom: e.target.value })} placeholder="Ex: ATRIUM GESTION" className="mt-1 rounded-none border-slate-300" data-testid="input-moa-nom" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={moa.type} onValueChange={(v) => updateNested('maitre_ouvrage', { type: v })}>
              <SelectTrigger className="mt-1 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="syndic">Syndic</SelectItem>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="copropriete">Copropriété</SelectItem>
                <SelectItem value="promoteur">Promoteur</SelectItem>
                <SelectItem value="collectivite">Collectivité</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={moa.email} onChange={(e) => updateNested('maitre_ouvrage', { email: e.target.value })} className="mt-1 rounded-none border-slate-300" />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input type="tel" value={moa.telephone} onChange={(e) => updateNested('maitre_ouvrage', { telephone: e.target.value })} className="mt-1 rounded-none border-slate-300" />
          </div>
        </div>
      </section>

      {/* Propriétaires */}
      <DynamicPersonList
        title="Propriétaires / Copropriétaires"
        hint="Ajoutez les personnes qui doivent avoir un accès de suivi au chantier."
        items={data.proprietaires}
        onChange={(v) => update({ proprietaires: v })}
        roleOptions={['Propriétaire', 'Copropriétaire', 'Syndic', 'Gestionnaire']}
        roleLabel="Rôle"
        testIdPrefix="prop"
        contactSources={['users']}
        contactRoleFilter="client"
      />

      {/* Équipe MOE */}
      <DynamicPersonList
        title="Équipe de maîtrise d'œuvre"
        hint="Qui d'autre travaille sur ce chantier ?"
        items={data.equipe}
        onChange={(v) => update({ equipe: v })}
        roleOptions={['AMO', 'BET Structure', 'BET Fluides', 'BET Thermique', 'SPS', 'Contrôleur technique', 'Économiste', 'Géomètre', 'OPC', 'Architecte décorateur', 'Paysagiste', 'Autre']}
        roleKey="mission"
        roleLabel="Mission"
        testIdPrefix="moe"
        contactSources={['users', 'equipe']}
      />
    </div>
  );
}

function DynamicPersonList({ title, hint, items, onChange, roleOptions, roleLabel = 'Rôle', roleKey = 'role', testIdPrefix = 'person', contactSources, contactRoleFilter }) {
  const add = (initial = {}) => onChange([...items, { nom: '', email: '', telephone: '', [roleKey]: roleOptions[0], ...initial }]);
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateAt = (i, patch) => onChange(items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  return (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800 mb-1">{title}</h3>
      {hint && <p className="text-xs text-slate-500 mb-3">{hint}</p>}

      {contactSources && (
        <div className="mb-3">
          <ContactSearch
            sources={contactSources}
            userRoleFilter={contactRoleFilter}
            placeholder="Rechercher un contact existant…"
            testId={`${testIdPrefix}-search`}
            onSelect={(c) => add({ nom: c.name || '', email: c.email || '', telephone: c.telephone || '' })}
            onCreateNew={(q) => add({ nom: q })}
          />
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-slate-400 italic">Aucun {title.toLowerCase()} pour le moment.</p>}
        {items.map((p, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1.2fr_1.2fr_1fr_1fr_auto] gap-2 items-end border border-slate-200 p-3">
            <div>
              <Label className="text-xs">Nom</Label>
              <Input value={p.nom} onChange={(e) => updateAt(i, { nom: e.target.value })} className="mt-1 rounded-none border-slate-300" data-testid={`${testIdPrefix}-${i}-nom`} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={p.email} onChange={(e) => updateAt(i, { email: e.target.value })} className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input value={p.telephone} onChange={(e) => updateAt(i, { telephone: e.target.value })} className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-xs">{roleLabel}</Label>
              <Select value={p[roleKey] || roleOptions[0]} onValueChange={(v) => updateAt(i, { [roleKey]: v })}>
                <SelectTrigger className="mt-1 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeAt(i)} className="rounded-none text-slate-400 hover:text-red-600">
              <Trash size={16} />
            </Button>
          </div>
        ))}
        <Button variant="outline" onClick={() => add()} className="rounded-none" data-testid={`${testIdPrefix}-add`}>
          <Plus size={14} className="mr-1" /> Ajouter manuellement
        </Button>
      </div>
    </section>
  );
}

function Step3Lots({ data, update }) {
  const add = (initial = {}) => update({ lots: [...data.lots, { code: '', nom: '', entreprise: '', email: '', montant: '', ...initial }] });
  const removeAt = (i) => update({ lots: data.lots.filter((_, idx) => idx !== i) });
  const updateAt = (i, patch) => update({ lots: data.lots.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Si des marchés sont déjà attribués, listez-les ici. Sinon l'IA les extraira de vos documents (DPGF, devis, factures).
      </p>

      <div className="mb-3">
        <ContactSearch
          sources={['entreprises', 'users']}
          userRoleFilter="entreprise"
          placeholder="Rechercher une entreprise existante…"
          testId="lot-entreprise-search"
          onSelect={(c) => add({ entreprise: c.name || '', email: c.email || '' })}
          onCreateNew={(q) => add({ entreprise: q })}
        />
      </div>

      <div className="space-y-3">
        {data.lots.length === 0 && <p className="text-sm text-slate-400 italic">Aucun lot pour le moment.</p>}
        {data.lots.map((l, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[0.6fr_1.2fr_1.3fr_1.3fr_0.9fr_auto] gap-2 items-end border border-slate-200 p-3">
            <div>
              <Label className="text-xs">N° lot</Label>
              <Input value={l.code} onChange={(e) => updateAt(i, { code: e.target.value })} placeholder="LOT01" className="mt-1 rounded-none border-slate-300" data-testid={`lot-${i}-code`} />
            </div>
            <div>
              <Label className="text-xs">Nom du lot</Label>
              <Input value={l.nom} onChange={(e) => updateAt(i, { nom: e.target.value })} placeholder="Gros Œuvre" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-xs">Entreprise</Label>
              <Input value={l.entreprise} onChange={(e) => updateAt(i, { entreprise: e.target.value })} className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-xs">Email entreprise</Label>
              <Input type="email" value={l.email} onChange={(e) => updateAt(i, { email: e.target.value })} className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-xs">Montant HT (€)</Label>
              <Input type="number" value={l.montant} onChange={(e) => updateAt(i, { montant: e.target.value })} className="mt-1 rounded-none border-slate-300" />
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeAt(i)} className="rounded-none text-slate-400 hover:text-red-600">
              <Trash size={16} />
            </Button>
          </div>
        ))}
        <Button variant="outline" onClick={() => add()} className="rounded-none" data-testid="lot-add">
          <Plus size={14} className="mr-1" /> Ajouter un lot
        </Button>
      </div>
      <p className="text-xs text-slate-500 italic">
        Pas de panique si vous ne connaissez pas tous les montants — l'IA les extraira des devis et factures.
      </p>
    </div>
  );
}

function Step4Finances({ data, updateNested }) {
  const f = data.finances;
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">Donnez les grandes lignes. L'IA affinera avec les factures uploadées.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Montant total marché HT (€)</Label>
          <Input type="number" value={f.montant_marche} onChange={(e) => updateNested('finances', { montant_marche: e.target.value })} placeholder="150 000" className="mt-1 rounded-none border-slate-300" data-testid="input-fin-total" />
        </div>
        <div>
          <Label>Déjà facturé HT (€)</Label>
          <Input type="number" value={f.facture} onChange={(e) => updateNested('finances', { facture: e.target.value })} placeholder="45 000" className="mt-1 rounded-none border-slate-300" />
        </div>
        <div>
          <Label>Déjà payé HT (€)</Label>
          <Input type="number" value={f.paye} onChange={(e) => updateNested('finances', { paye: e.target.value })} placeholder="30 000" className="mt-1 rounded-none border-slate-300" />
        </div>
      </div>
      <div>
        <Label>Factures en litige ou impayées ?</Label>
        <Textarea rows={3} value={f.litige} onChange={(e) => updateNested('finances', { litige: e.target.value })} placeholder="Décrivez la situation (optionnel)" className="mt-1 rounded-none border-slate-300" />
      </div>
      <div>
        <Label>Retenue de garantie en cours ?</Label>
        <div className="flex gap-4 mt-2">
          {['oui', 'non', 'nsp'].map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input type="radio" name="retenue" value={v} checked={f.retenue === v} onChange={() => updateNested('finances', { retenue: v })} />
              {v === 'oui' ? 'Oui' : v === 'non' ? 'Non' : 'Ne sais pas'}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step5Chantier({ data, update }) {
  return (
    <div className="space-y-5">
      <div>
        <Label>Avancement global estimé</Label>
        <Select value={String(data.avancement)} onValueChange={(v) => update({ avancement: Number(v) })}>
          <SelectTrigger className="mt-1 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Pas encore commencé</SelectItem>
            <SelectItem value="10">Début de chantier (~10%)</SelectItem>
            <SelectItem value="25">Travaux en cours (~25%)</SelectItem>
            <SelectItem value="50">Mi-parcours (~50%)</SelectItem>
            <SelectItem value="75">Travaux avancés (~75%)</SelectItem>
            <SelectItem value="90">Finitions (~90%)</SelectItem>
            <SelectItem value="100">Travaux terminés — en attente de réception</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Problèmes en cours ?</Label>
        <Textarea rows={4} value={data.problemes} onChange={(e) => update({ problemes: e.target.value })} placeholder="Retards, malfaçons, litiges, réserves non levées…" className="mt-1 rounded-none border-slate-300" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Fréquence des réunions</Label>
          <Select value={data.frequence_reunions} onValueChange={(v) => update({ frequence_reunions: v })}>
            <SelectTrigger className="mt-1 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hebdomadaire">Hebdomadaire</SelectItem>
              <SelectItem value="bimensuel">Toutes les 2 semaines</SelectItem>
              <SelectItem value="mensuel">Mensuel</SelectItem>
              <SelectItem value="ponctuel">Ponctuel</SelectItem>
              <SelectItem value="aucun">Pas de réunion régulière</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Jour</Label>
          <Select value={data.jour_reunion} onValueChange={(v) => update({ jour_reunion: v })}>
            <SelectTrigger className="mt-1 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'].map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Heure</Label>
          <Input type="time" value={data.heure_reunion} onChange={(e) => update({ heure_reunion: e.target.value })} className="mt-1 rounded-none border-slate-300" />
        </div>
      </div>
      <div>
        <Label>Prochaine réunion prévue</Label>
        <Input type="date" value={data.prochaine_reunion} onChange={(e) => update({ prochaine_reunion: e.target.value })} className="mt-1 rounded-none border-slate-300" />
      </div>
      <div>
        <Label>Contexte réglementaire (optionnel)</Label>
        <Textarea rows={3} value={data.contexte_reglementaire} onChange={(e) => update({ contexte_reglementaire: e.target.value })} placeholder="Arrêté de péril, permis de construire, déclaration préalable, ABF…" className="mt-1 rounded-none border-slate-300" />
      </div>
    </div>
  );
}

function Step6Documents({ data, onUpload, onRemove, totalFiles }) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Uploadez <strong>tous</strong> les documents du dossier. L'IA analysera chaque document par OCR, identifiera son type, et extraira les informations pour pré-remplir l'application.
      </p>
      {DOC_CATEGORIES.map((cat) => (
        <UploadCategory
          key={cat.key}
          cat={cat}
          files={data.files[cat.key] || []}
          onUpload={(fl) => onUpload(cat.key, fl)}
          onRemove={(id) => onRemove(cat.key, id)}
        />
      ))}
      <div className="bg-[#FFF2EB] border border-[#C75B39]/30 p-4 flex items-center gap-3">
        <CheckCircle size={20} className="text-[#C75B39]" weight="fill" />
        <p className="text-sm"><strong>{totalFiles}</strong> fichier(s) prêts à être analysés.</p>
      </div>
    </div>
  );
}

function UploadCategory({ cat, files, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div className="border border-slate-200">
      <div className="p-4 border-b border-slate-200">
        <h3 className="text-sm font-semibold">{cat.label}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{cat.hint}</p>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onUpload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`p-6 text-center cursor-pointer transition-colors ${drag ? 'bg-[#FFF2EB]' : 'bg-slate-50 hover:bg-slate-100'}`}
        data-testid={`upload-zone-${cat.key}`}
      >
        <input ref={inputRef} type="file" multiple onChange={(e) => onUpload(e.target.files)} className="hidden" />
        <FileArrowUp size={28} className="mx-auto text-slate-400 mb-2" />
        <p className="text-sm text-slate-600">Glissez vos fichiers ou cliquez pour sélectionner</p>
        <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, images — plusieurs fichiers à la fois</p>
      </div>
      {files.length > 0 && (
        <div className="divide-y divide-slate-100">
          {files.map((f) => (
            <div key={f.tmpId} className="flex items-center gap-3 px-4 py-2 text-sm">
              <FileArrowUp size={14} className={f.error ? 'text-red-500' : f.uploaded ? 'text-emerald-600' : 'text-slate-400'} />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-xs text-slate-400">{formatSize(f.size)}</span>
              {f.uploading && <span className="text-xs text-slate-500">⏳</span>}
              {f.uploaded && <CheckCircle size={14} className="text-emerald-600" weight="fill" />}
              {f.error && <Warning size={14} className="text-red-500" weight="fill" />}
              <button onClick={() => onRemove(f.tmpId)} className="text-slate-400 hover:text-red-600">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Step7Instructions({ data, update, totalFiles }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        Y a-t-il des choses spécifiques que l'IA doit savoir pour bien traiter votre dossier ?
      </p>
      <div>
        <Label>Contexte particulier</Label>
        <Textarea
          rows={5}
          value={data.contexte}
          onChange={(e) => update({ contexte: e.target.value })}
          placeholder="Ex: Le lot structure a été repris par une autre entreprise en cours de chantier. Les 3 premiers comptes rendus ont été faits par l'ancien architecte…"
          className="mt-1 rounded-none border-slate-300"
        />
      </div>
      <div>
        <Label>Priorités pour ce chantier</Label>
        <Textarea
          rows={3}
          value={data.instructions}
          onChange={(e) => update({ instructions: e.target.value })}
          placeholder="Ex: Priorité sur la mise en sécurité. Le syndic attend un reporting financier mensuel."
          className="mt-1 rounded-none border-slate-300"
        />
      </div>

      <div className="bg-slate-50 border border-slate-200 p-4">
        <h4 className="font-semibold mb-3 text-sm">Récapitulatif de l'import</h4>
        <ul className="text-sm space-y-1 text-slate-700">
          <li>📍 <strong>{data.nom || '—'}</strong> — {data.adresse || '—'}</li>
          <li>🏗 {data.type_projet} — Phase {data.phase}</li>
          <li>👤 MOA : {data.maitre_ouvrage.nom || '—'}</li>
          <li>👥 {data.proprietaires.length} propriétaire(s)</li>
          <li>👷 {data.equipe.length} intervenant(s) MOE</li>
          <li>🏢 {data.lots.length} lot(s) / entreprise(s)</li>
          <li>📎 {totalFiles} document(s) à analyser</li>
        </ul>
      </div>

      <p className="text-xs text-slate-500 text-center italic">
        L'analyse peut prendre 2 à 5 minutes selon le nombre de documents.
      </p>
    </div>
  );
}

// ==================== PROGRESS / RÉSULTAT ====================

function ImportProgress({ chantierId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('en_cours');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/import-status`, { withCredentials: true });
        if (!alive) return;
        setStatus(data.import_status || 'en_cours');
        if (data.import_status === 'termine') setAnalysis(data.import_analysis);
        if (data.import_status === 'erreur') setError(data.import_error || 'Erreur inconnue');
      } catch (e) { /* noop */ }
    };
    poll();
    const i = setInterval(() => {
      if (status === 'termine' || status === 'erreur') return;
      poll();
    }, 5000);
    return () => { alive = false; clearInterval(i); };
  }, [chantierId, status]);

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="import-progress-page">
        {status === 'en_cours' && (
          <div className="text-center py-16">
            <div className="w-16 h-16 border-4 border-[#C75B39] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>L'IA analyse vos documents…</h2>
            <p className="text-slate-600">Cela peut prendre 2 à 5 minutes. Vous pouvez fermer cette page — l'analyse continue en arrière-plan.</p>
            <Button onClick={() => navigate(`/chantiers/${chantierId}`)} variant="outline" className="rounded-none mt-6" data-testid="btn-go-project">
              Voir le projet maintenant
            </Button>
          </div>
        )}

        {status === 'erreur' && (
          <div className="card-swiss p-6 border-red-300">
            <div className="flex items-center gap-3 mb-3">
              <Warning size={24} className="text-red-600" weight="fill" />
              <h2 className="text-xl font-bold text-red-700">Erreur d'analyse</h2>
            </div>
            <p className="text-sm text-slate-700 mb-4">{error}</p>
            <p className="text-sm text-slate-600">Le projet a bien été créé mais l'analyse IA a échoué. Vous pouvez continuer manuellement.</p>
            <Button onClick={() => navigate(`/chantiers/${chantierId}`)} className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none mt-4">
              Voir le projet →
            </Button>
          </div>
        )}

        {status === 'termine' && analysis && (
          <ImportResult chantierId={chantierId} analysis={analysis} />
        )}
      </div>
    </Layout>
  );
}

function ImportResult({ chantierId, analysis }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="import-result">
      <div className="flex items-center gap-3 mb-2">
        <CheckCircle size={28} className="text-emerald-600" weight="fill" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Import terminé</h1>
      </div>

      {analysis.synthese && (
        <div className="card-swiss p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide mb-2 text-slate-700">Synthèse</h3>
          <p className="text-slate-800 whitespace-pre-line">{analysis.synthese}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Lots" value={(analysis.lots_identifies || []).length} />
        <StatBox label="Factures" value={(analysis.factures_identifiees || []).length} />
        <StatBox label="Réserves" value={(analysis.reserves_identifiees || []).length} />
        <StatBox label="Tâches planning" value={(analysis.planning_estime || []).length} />
      </div>

      {(analysis.points_attention || []).length > 0 && (
        <div className="card-swiss p-5 border-l-4 border-[#D4880F]">
          <h3 className="text-sm font-semibold uppercase tracking-wide mb-3 text-[#D4880F]">⚠ Points d'attention</h3>
          <ul className="space-y-2 text-sm text-slate-800 list-disc list-inside">
            {analysis.points_attention.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      {(analysis.documents_manquants || []).length > 0 && (
        <div className="card-swiss p-5 border-l-4 border-[#0047FF]">
          <h3 className="text-sm font-semibold uppercase tracking-wide mb-3 text-[#0047FF]">📋 Documents manquants</h3>
          <ul className="space-y-2 text-sm text-slate-800 list-disc list-inside">
            {analysis.documents_manquants.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}

      {analysis.coherence && Object.keys(analysis.coherence).length > 0 && (
        <div className="card-swiss p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide mb-3 text-slate-700">🔍 Vérification de cohérence</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(analysis.coherence).map(([k, v]) => (
              <div key={k} className="flex items-start gap-2">
                <span>{String(v).startsWith('OK') ? '✅' : '⚠'}</span>
                <div>
                  <div className="font-medium capitalize">{k.replace(/_/g, ' ')}</div>
                  <div className="text-slate-600">{v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => navigate(`/chantiers/${chantierId}?tab=suivi`)} className="bg-[#C75B39] hover:bg-[#A94B2E] rounded-none" data-testid="btn-see-project">
          Voir le projet <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="card-swiss p-4 text-center">
      <div className="text-2xl font-bold text-[#C75B39]">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}
